import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueryClient} from '@github-ui/react-query'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo, useRef, useState} from 'react'

import {buildPullRequestUserActionContext} from '../../analytics/pull-request-user-action'
import {getUnstackInactiveText} from '../../helpers/unstack-inactive-text'
import {useCopyStackLinks} from '../../hooks/use-copy-stack-links'
import {useUnstackPullRequestsMutation} from '../../mutations/use-unstack-pull-requests-mutation'
import {repoStackDetailRoute} from '../../routes/repo-stack-detail-route'
import {stacksListPath} from '../../routes/repo-stacks-list-route'
import {StackActionsMenu} from '../StackActionsMenu'
import {UnstackDialog} from '../UnstackButtonDialog'

/** The stack detail page's "..." menu: copy the pull request links, or unstack. */
export function StackDetailActions() {
  const {owner, repo} = useRouteParams(repoStackDetailRoute)
  const {data} = useSuspenseRouteQuery(repoStackDetailRoute, 'mainQuery')
  if (!data.stack || data.unstackPullRequestNumber === undefined) return null

  // The unstack endpoint is addressed through any pull request in the stack.
  return (
    <PageDataContextProvider basePageDataUrl={`/${owner}/${repo}/pull/${data.unstackPullRequestNumber}`}>
      <StackDetailActionsMenu />
    </PageDataContextProvider>
  )
}

function StackDetailActionsMenu() {
  const {owner, repo} = useRouteParams(repoStackDetailRoute)
  const {data, refetch, isFetching} = useSuspenseRouteQuery(repoStackDetailRoute, 'mainQuery')
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {mutate: unstackPullRequests, isPending} = useUnstackPullRequestsMutation()
  const pulls = useMemo(
    () => data.pullRequests.map(pull => ({number: pull.number, title: pull.title, url: pull.permalink ?? ''})),
    [data.pullRequests],
  )
  const {copyStatus, copyStackLinks, resetCopyStatus} = useCopyStackLinks({pulls})

  const stack = data.stack
  if (!stack) return null

  // The page may have loaded a while ago, and auto-merge or merged pull requests change what unstacking does.
  // Refresh each time the menu opens, so the confirmation is up to date.
  const refreshStack = () => void refetch({cancelRefetch: false})

  const onUnstack = () => {
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_detail',
        action: 'unstack_click',
        data: {
          stack: {id: Number(stack.id), number: Number(stack.number), created_at: stack.createdAt},
          stack_size: stack.size,
        },
      }),
    )
    setIsConfirmingUnstack(true)
  }

  const onConfirmUnstack = () => {
    setErrorMessage('')
    unstackPullRequests(
      {stackId: Number(stack.id)},
      {
        onError: error => setErrorMessage(error.message),
        onSuccess: async () => {
          // Drop the cached list rather than marking it stale, so going back never shows the old stack, even briefly.
          queryClient.removeQueries({predicate: query => query.queryKey[1] === 'repoStacksListRoute'})
          const result = await refetch()
          // The stack is deleted once no pull requests remain. Merged and queued pull requests stay stacked.
          if (isResponseError(result.error) && result.error.response.status === 404) {
            navigate(stacksListPath({owner, repo}))
            return
          }
          if (result.isError) {
            setErrorMessage('Something unexpected happened. Please refresh the page.')
            return
          }
          setIsConfirmingUnstack(false)
        },
      },
    )
  }

  return (
    <>
      <StackActionsMenu
        anchorRef={anchorRef}
        copyStatus={copyStatus}
        onCopy={isFeatureEnabled('pull_request_stacks_copy_links') ? () => void copyStackLinks() : undefined}
        onMenuOpen={refreshStack}
        onMenuToggle={resetCopyStatus}
        onUnstack={onUnstack}
        unstackInactiveText={getUnstackInactiveText({
          viewerCanUnstack: data.viewerCanUnstack,
          allPullsLocked: data.allPullRequestsLocked,
        })}
      />
      <UnstackDialog
        returnFocusRef={anchorRef}
        isConfirmingUnstack={isConfirmingUnstack}
        isLoading={isPending}
        errorMessage={errorMessage}
        isAutoMergeEnabled={data.autoMergeEnabled}
        isCheckingAutoMerge={isFetching}
        someLockedPulls={data.somePullRequestsLocked}
        onCancelUnstack={() => {
          setIsConfirmingUnstack(false)
          setErrorMessage('')
        }}
        onConfirmUnstack={onConfirmUnstack}
      />
    </>
  )
}
